import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCredentialsByType } from '../../hooks/useCredentials';
import { Credential } from '../../types/credentials';
import { emailService } from '../../services/emailService';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import CredentialEditor from './CredentialEditor';
import CredentialsManager from './CredentialsManager';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import toast from 'react-hot-toast';
import { 
  EnvelopeIcon, 
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  ServerIcon,
  ExclamationCircleIcon,
  KeyIcon
} from '@heroicons/react/24/outline';

const EmailSettings: React.FC = () => {
  const { user } = useAuth();
  const { credentials, loading, create, update, remove, refresh } = useCredentialsByType('smtp');
  
  const [selectedCredentialId, setSelectedCredentialId] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState<Credential | null>(null);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [loadingDefault, setLoadingDefault] = useState(true);
  const [credentialToDelete, setCredentialToDelete] = useState<Credential | null>(null);
  const [showCredentialsManager, setShowCredentialsManager] = useState(false);

  // Betöltjük az alapértelmezett SMTP credential-t
  useEffect(() => {
    const loadDefaultCredential = async () => {
      if (!user?.organizationId) return;
      
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', user.organizationId));
        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          if (orgData.defaultSmtpCredentialId) {
            setSelectedCredentialId(orgData.defaultSmtpCredentialId);
          }
        }
      } catch (error) {
        console.error('Error loading default SMTP credential:', error);
      } finally {
        setLoadingDefault(false);
      }
    };

    loadDefaultCredential();
  }, [user?.organizationId]);

  const handleCreateCredential = () => {
    setEditingCredential(null);
    setIsEditorOpen(true);
  };

  const handleEditCredential = (credential: Credential) => {
    setEditingCredential(credential);
    setIsEditorOpen(true);
  };

  const handleSelectCredential = async (credentialId: string) => {
    setSelectedCredentialId(credentialId);
    
    // Automatikus mentés az organizationhoz
    if (!user?.organizationId) return;
    
    try {
      const orgRef = doc(db, 'organizations', user.organizationId);
      await updateDoc(orgRef, {
        defaultSmtpCredentialId: credentialId,
        updatedAt: new Date()
      });
      toast.success('Alapértelmezett SMTP beállítva');
    } catch (error) {
      console.error('Error saving default SMTP credential:', error);
      toast.error('Hiba az alapértelmezett SMTP mentése során');
    }
  };

  const handleDeleteClick = (credential: Credential) => {
    setCredentialToDelete(credential);
  };

  const handleConfirmDelete = async () => {
    if (!credentialToDelete) return;
    
    try {
      await remove(credentialToDelete.id);
      if (selectedCredentialId === credentialToDelete.id) {
        setSelectedCredentialId(null);
        // Töröljük az organizationból is
        if (user?.organizationId) {
          const orgRef = doc(db, 'organizations', user.organizationId);
          await updateDoc(orgRef, {
            defaultSmtpCredentialId: null,
            updatedAt: new Date()
          });
        }
      }
      toast.success('SMTP credential törölve');
      setCredentialToDelete(null);
    } catch (error) {
      console.error('Error deleting credential:', error);
      toast.error('Hiba a credential törlése során');
      setCredentialToDelete(null);
    }
  };


  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Adjon meg egy email címet a teszthez!');
      return;
    }

    if (!selectedCredentialId) {
      toast.error('Válasszon ki egy SMTP credential-t!');
      return;
    }

    setTesting(true);
    try {
      await emailService.sendTestEmail(testEmail, user?.organizationId, selectedCredentialId);
      toast.success('Teszt email sikeresen elküldve!');
    } catch (error: any) {
      toast.error(`Teszt email küldése sikertelen: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading || loadingDefault) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Email / SMTP Beállítások</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          SMTP credentials kezelése email küldéshez
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <ExclamationCircleIcon className="h-6 w-6 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Credentials rendszer</p>
              <p>
                Az SMTP beállítások biztonságosan titkosítva vannak tárolva a Firestore-ban. 
                A Firebase Functions automatikusan betölti és használja a kiválasztott credential-t email küldéskor.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCredentialsManager(true)}
            className="ml-4 inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-600 text-sm font-medium rounded-md text-blue-700 dark:text-blue-300 bg-white dark:bg-blue-900/30 hover:bg-blue-50 dark:hover:bg-blue-900/50 transition-colors"
          >
            <KeyIcon className="h-4 w-4 mr-2" />
            Credentials Manager
          </button>
        </div>
      </div>

      {/* SMTP Credentials List */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ServerIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">SMTP Credentials</h4>
          </div>
          <button
            onClick={handleCreateCredential}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Új SMTP
          </button>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {credentials.length === 0 ? (
            <div className="p-8 text-center">
              <ServerIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                Nincs SMTP credential
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Kezdje el egy új SMTP credential létrehozásával.
              </p>
              <div className="mt-6">
                <button
                  onClick={handleCreateCredential}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Új SMTP credential
                </button>
              </div>
            </div>
          ) : (
            credentials.map((credential) => (
              <div
                key={credential.id}
                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                  selectedCredentialId === credential.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <input
                      type="radio"
                      name="smtp-credential"
                      checked={selectedCredentialId === credential.id}
                      onChange={() => handleSelectCredential(credential.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {credential.name}
                        </h5>
                        {credential.isActive && (
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {credential.data?.host}:{credential.data?.port} • {credential.data?.user}
                      </p>
                      {credential.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {credential.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditCredential(credential)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                      title="Szerkesztés"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(credential)}
                      className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Törlés"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Test Email */}
      {credentials.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Teszt email küldése
          </h4>
          
          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email cím
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="test@example.com"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleTestEmail}
                disabled={testing || !testEmail || !selectedCredentialId}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Küldés...
                  </>
                ) : (
                  <>
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    Teszt küldés
                  </>
                )}
              </button>
            </div>
          </div>

          {!selectedCredentialId && credentials.length > 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
              ⚠️ Válasszon ki egy SMTP credential-t a teszt email küldéséhez
            </p>
          )}
        </div>
      )}

      {/* Credential Editor Modal */}
      {isEditorOpen && (
        <CredentialEditor
          type="smtp"
          credential={editingCredential || undefined}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingCredential(null);
            refresh(); // Frissítjük a listát
          }}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={!!credentialToDelete}
        onClose={() => setCredentialToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="SMTP Credential törlése"
        deleteMessage={`Biztosan törli a(z) "${credentialToDelete?.name}" SMTP credential-t?`}
        deleteWarning="Ez a credential a központi Credentials Manager-ből is törlésre kerül, és minden flow vagy funkció amely ezt használja, többé nem fog működni."
        danger={true}
      />

      {/* Credentials Manager Modal */}
      {showCredentialsManager && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowCredentialsManager(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Credentials Manager
                </h2>
                <button
                  onClick={() => setShowCredentialsManager(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <CredentialsManager />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { EmailSettings };
export default EmailSettings;
